import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useStore } from 'zustand';
import { useUnityBuildStore, defaultProject } from '../stores/store';
import { useAssemblyStore } from '../stores/AssemblyStore';
import { useInferenceStore } from '../stores/inferenceStore';
import { ResetStates } from '../stores/resetStates';
import { useFetchUser } from '../hooks/useFetchUsers';
import { UbAccountService } from '../api/UbAccountService';
import { useKeyBoardEventStore } from '../stores/keyEventStore';
import { useQueryClient } from 'react-query';
import { useLocalStorage } from '../stores/localStorage';
import LogIn from '../components/LogIn';
// Create a global flag to track authentication state
// This helps components detect auth failures even before context is available
let globalIsAuthenticated = true;

// Create a global to store handler for stopped animation frames
let animationFrameIds: number[] = [];

// Helper to stop all animation frames
const stopAllAnimationFrames = () => {
  // First, cancel all registered animation frames
  animationFrameIds.forEach(id => {
    try {
      cancelAnimationFrame(id);
    } catch (e) {
      // Ignore errors
    }
  });
  animationFrameIds = [];

  // Then override requestAnimationFrame to prevent new ones
  const originalRequestAnimationFrame = window.requestAnimationFrame;
  window.requestAnimationFrame = function (callback) {
    // If authenticated, allow animation frames
    if (globalIsAuthenticated) {
      const id = originalRequestAnimationFrame(callback);
      animationFrameIds.push(id);
      return id;
    }
    // Otherwise, prevent new ones
    return 0;
  };

  // Store the original for later
  (window as any).__originalRequestAnimationFrame = originalRequestAnimationFrame;
};

// Helper to restore animation frames
const restoreAnimationFrames = () => {
  // Restore original requestAnimationFrame if it was saved
  if ((window as any).__originalRequestAnimationFrame) {
    window.requestAnimationFrame = (window as any).__originalRequestAnimationFrame;
    (window as any).__originalRequestAnimationFrame = null;
  }
};

interface AuthContextType {
  isAuthenticated: boolean;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const queryClient = useQueryClient();

  // Get store instances
  const store = useStore(useUnityBuildStore);
  const store1 = useStore(useAssemblyStore);
  const store3 = useStore(useInferenceStore);
  const keyBoardEventStore = useStore(useKeyBoardEventStore);
  const { setIsLoggedIn, setProjectListIsMounted, setSelectedProject } =
    useStore(useUnityBuildStore);
  const { setSessionToken } = useStore(useLocalStorage);
  const { data: user } = useFetchUser();

  const logout = useCallback(async () => {
    if (isLoggingOut) return; // Prevent multiple logout attempts

    try {
      setIsLoggingOut(true);

      // Update global flag
      globalIsAuthenticated = false;

      // Stop all animation frames to prevent further API calls
      stopAllAnimationFrames();

      // Cancel all existing timeouts to prevent new API calls
      const highestId = window.setTimeout(() => {}, 0);
      for (let i = 0; i < highestId; i++) {
        window.clearTimeout(i);
      }

      if (user && user.user) {
        // Log out user on backend
        try {
          await UbAccountService.logAccountUserOut(user.user.id);
        } catch (e) {
          console.error('Error logging out from server:', e);
          // Continue with logout process even if backend call fails
        }
      }

      // Reset all states
      ResetStates.resetMasterProjectViewStates(store, store1, store3, keyBoardEventStore);

      // Import and reset all stores
      try {
        const { useSelectionStore } = require('../states/SelectionStore');
        const { useChatWindowStore } = require('../states/chatWindowStore');
        const { useFolderStore } = require('../states/folderStore');
        const { useGlobalItemStore } = require('../states/globalItemStore');

        // Reset all stores to their initial state
        useSelectionStore.getState().clearSelection();
        useChatWindowStore.getState().setMessages([]);
        useFolderStore.getState().setCurrentFolder(null);
        useGlobalItemStore.getState().setCurrentItem(null);

        // Reset the Unity Build Store
        useUnityBuildStore.setState({
          isLoggedIn: false,
          selectedProject: defaultProject,
          projectListIsMounted: false,
          selectedObject: null,
          selectedEstimate: null,
          selectedContract: null,
          selectedChangeOrder: null,
          selectedBreakout: null,
          // All other state values will remain at their default values
        });

        // Reset Assembly Store
        useAssemblyStore.setState({
          currentGlobalAssembly: null,
          currentTakeoffAssembly: null,
          filteredItems: [],
          assemblyItems: [],
          isCreatingAssembly: false,
        });

        // Reset Inference Store
        useInferenceStore.setState({
          inferenceCountObjects: [],
        });

        // Reset KeyBoardEventStore
        useKeyBoardEventStore.setState({
          isMeasuring: false,
          showChatWindow: false,
        });
      } catch (e) {
        console.error('Error resetting stores:', e);
      }

      // Reset local state
      setIsAuthenticated(false);
      setIsLoggedIn(false);
      setSessionToken('');
      setSelectedProject(defaultProject);
      setProjectListIsMounted(false);

      // Clear all localStorage data
      localStorage.clear();
      // Also use our dedicated method to ensure proper clearing
      const { useLocalStorage } = require('../states/localStorage');
      useLocalStorage.getState().clearAllStorageData();

      // Reset the sync connector store state
      try {
        const { useSyncConnectorStore } = require('../hooks/useSyncConnector');
        if (typeof useSyncConnectorStore.setState === 'function') {
          useSyncConnectorStore.setState({ syncMap: {} });
        }
      } catch (e) {
        console.error('Error resetting sync connector store:', e);
      }

      // Cancel all queries immediately and ensure complete cache removal
      queryClient.cancelQueries();
      queryClient.clear();
      queryClient.resetQueries();
      queryClient.removeQueries();

      // Force a hard redirect to login page instead of using React Router
      window.location.href = '/login';
    } catch (error) {
      console.error('Error during logout:', error);
      // Force a hard reload to the login page as a last resort
      window.location.href = '/login';
    } finally {
      setIsLoggingOut(false);
    }
  }, [
    user,
    store,
    store1,
    store3,
    keyBoardEventStore,
    setIsLoggedIn,
    setProjectListIsMounted,
    setSessionToken,
    setSelectedProject,
    queryClient,
    isLoggingOut,
  ]);

  // Add global error handler for authentication errors
  useEffect(() => {
    const handleAuthError = (error: any) => {
      if (
        (error?.code === 16 && error?.message === 'authentication failed') ||
        (error?.code === 13 && error?.message === 'failed to authenticate user')
      ) {
        console.error('AUTH ERROR DETECTED:', error);
        // Only attempt logout if not already doing so
        if (isAuthenticated && !isLoggingOut) {
          // Create a countdown dialog
          const showCountdownDialog = () => {
            let seconds = 5;

            // Create modal container
            const modal = document.createElement('div');
            modal.style.position = 'fixed';
            modal.style.zIndex = '9999';
            modal.style.left = '0';
            modal.style.top = '0';
            modal.style.width = '100%';
            modal.style.height = '100%';
            modal.style.backgroundColor = 'rgba(0,0,0,0.5)';
            modal.style.display = 'flex';
            modal.style.alignItems = 'center';
            modal.style.justifyContent = 'center';

            // Create modal content
            const modalContent = document.createElement('div');
            modalContent.style.backgroundColor = 'white';
            modalContent.style.padding = '30px';
            modalContent.style.borderRadius = '5px';
            modalContent.style.width = '400px';
            modalContent.style.textAlign = 'center';
            modalContent.style.boxShadow = '0 4px 8px rgba(0, 0, 0, 0.2)';

            // Create title
            const title = document.createElement('h2');
            title.textContent = 'Session Expired';
            title.style.margin = '0 0 15px 0';
            title.style.color = '#333';

            // Create message
            const message = document.createElement('p');
            message.textContent =
              'Your authentication session has expired. You will be logged out in:';
            message.style.margin = '0 0 20px 0';
            message.style.color = '#555';

            // Create countdown element
            const countdown = document.createElement('div');
            countdown.textContent = `${seconds} seconds`;
            countdown.style.fontSize = '24px';
            countdown.style.fontWeight = 'bold';
            countdown.style.color = '#e74c3c';
            countdown.style.margin = '0 0 20px 0';

            // Add all elements to modal
            modalContent.appendChild(title);
            modalContent.appendChild(message);
            modalContent.appendChild(countdown);
            modal.appendChild(modalContent);

            // Add modal to body
            document.body.appendChild(modal);

            // Start countdown
            const interval = setInterval(() => {
              seconds--;
              countdown.textContent = `${seconds} seconds`;

              if (seconds <= 0) {
                clearInterval(interval);
                document.body.removeChild(modal);
                logout();
              }
            }, 1000);
            // Then return the logIn component
            <LogIn />;
          };

          showCountdownDialog();
        }
      }
    };

    // Add global unhandled error listener
    const handleGlobalError = (event: ErrorEvent) => {
      const errorText = event.error?.toString() || event.message || '';
      if (
        errorText.includes('authentication failed') ||
        errorText.includes('failed to authenticate')
      ) {
        console.error('Global AUTH ERROR detected:', errorText);
        if (isAuthenticated && !isLoggingOut) {
          handleAuthError({ code: 16, message: 'authentication failed' });
        }
      }
    };
    window.addEventListener('error', handleGlobalError);

    // Add error handler to query client's default error handler
    const defaultErrorHandler = queryClient.getDefaultOptions()?.queries?.onError;
    queryClient.setDefaultOptions({
      queries: {
        ...queryClient.getDefaultOptions()?.queries,
        retry: (failureCount, error) => {
          // Don't retry on auth errors
          if (
            (error as any)?.code === 16 ||
            (error as any)?.code === 13 ||
            (error as any)?.message?.includes('authentication failed') ||
            (error as any)?.message?.includes('failed to authenticate')
          ) {
            console.error('AUTH ERROR in retry:', error);
            return false;
          }
          return failureCount < 3;
        },
        onError: error => {
          handleAuthError(error);
          defaultErrorHandler?.(error);
        },
      },
    });

    // Also handle errors from the query cache
    const unsubscribe = queryClient.getQueryCache().subscribe((event: any) => {
      if (event?.type === 'error' && event?.error) {
        handleAuthError(event.error);
      }
    });

    return () => {
      unsubscribe();
      window.removeEventListener('error', handleGlobalError);
      // Restore the original error handler
      queryClient.setDefaultOptions({
        queries: {
          ...queryClient.getDefaultOptions()?.queries,
          onError: defaultErrorHandler,
        },
      });
      // Restore animation frames
      restoreAnimationFrames();
    };
  }, [logout, queryClient, isAuthenticated, isLoggingOut]);

  return (
    <AuthContext.Provider value={{ isAuthenticated, logout }}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
